import { LOCATION_CHANGE } from 'react-router-redux';
import { CashflowCapitalItem, CashflowCapitalItemGroup, ForesightScenarioBaselineCalculation, RequestStatus, defaults, entities } from 'typed/api';
import {
    childrenReducer, combineReducers, entityAddAllReducer, entityAddReducer,
    entityDeleteAllReducer, entityDeleteReducer, entityEditAllReducer, entityEditReducer, entityLoadAllReducer, entityLoadReducer,
    entityVoidAllReducer, networkActionReducer, sortableEntitiesReducer, sortedEntitiesReducer, switchReducers, synchronizableEnumReducer
} from './utils';
import { Action } from 'typed/actions';
import { deepAssign } from 'typed/utils';


// TODO improve how `switchReducers` and `combineReducers` work; both of them have `defaultState` parameter, which is applied before any call
// so their children reducers will never face an empty state and therefore don't need `defaultState`
// so there should be special type of reducer without empty state option, and special versions of entity*Reducer without `defaultState` generating those special reducers
// but that's a lot of work...
// for now let's reduce amount of visual noise and accesses to `defaults` (which actually might be slow since every attempt to access creates a new copy of an entity)
// with these very short aliases
const
    m = defaults.ForesightScenarioReport,
    ms = defaults.NetworkArrayOf(m),
    l = defaults.ForesightScenarioLevelReport,
    ls = [l].slice(0, 0),
    ets: entities.ForesightScenarioElementType[] = [],
    ars: entities.BusinessSimulatorCashflowAccountReceivablesDistribution[] = [],
    srs: entities.BusinessSimulatorCashflowSalesReceivablesDistribution[] = [],
    civs = defaults.NetworkPaginationSliceOf(defaults.BusinessSimulatorCashflowCapitalItemValue),
    e = defaults.ForesightScenarioElementReport,
    es = [e].slice(0, 0),
    f = defaults.ForesightScenarioFactor,
    fs = [f].slice(0, 0),

    elements = childrenReducer(es,
        combineReducers(e, {
            Updated: (state: Date = new Date(), action: Action<'ADD_FORESIGHT_SCENARIO_ELEMENT' | 'EDIT_FORESIGHT_SCENARIO_ELEMENT' | 'ADD_FORESIGHT_SCENARIO_FACTOR' | 'EDIT_FORESIGHT_SCENARIO_FACTOR' | 'DELETE_FORESIGHT_SCENARIO_FACTOR'>) =>
                action.type === 'ADD_FORESIGHT_SCENARIO_ELEMENT' || action.type === 'EDIT_FORESIGHT_SCENARIO_ELEMENT' ||
                    action.type === 'ADD_FORESIGHT_SCENARIO_FACTOR' || action.type === 'EDIT_FORESIGHT_SCENARIO_FACTOR' || action.type === 'DELETE_FORESIGHT_SCENARIO_FACTOR' ?
                    action.payload.status === RequestStatus.SUCCEEDED ? new Date() : state :
                    state,
            Factors: sortableEntitiesReducer(fs, null, switchReducers(fs,
                entityLoadReducer(fs, 'LOAD_FORESIGHT_SCENARIO_FACTOR'),
                entityAddReducer(fs, 'ADD_FORESIGHT_SCENARIO_FACTOR'),
                entityEditReducer(fs, 'EDIT_FORESIGHT_SCENARIO_FACTOR'),
                entityDeleteReducer(fs, 'DELETE_FORESIGHT_SCENARIO_FACTOR')
            ))
        }),
        (state, action) => {
            switch (action.type) {
                case 'LOAD_FORESIGHT_SCENARIO_FACTOR':
                case 'ADD_FORESIGHT_SCENARIO_FACTOR':
                case 'EDIT_FORESIGHT_SCENARIO_FACTOR':
                case 'DELETE_FORESIGHT_SCENARIO_FACTOR':
                    return state.getIndexByID(action.payload.parentID);

                default:
                    return state.getIndexByID(action.payload && ((action.payload as any).response || (action.payload as any).entityID));
            }
        }
    );

export default combineReducers({
    scenarioID: (state, action) => {
        const nextState = state || null;

        if (action.type === LOCATION_CHANGE && action.payload.pathname.startsWith('/scenarios/')) {
            return +action.payload.pathname.match(/^\/scenarios\/(\d+)?/)[1] || null;
        }

        return nextState;
    },
    cashflowCapitalItemGroups: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.BusinessSimulatorCashflowCapitalItemGroup), 'LOAD_ALL_BUSINESS_SIMULATOR_CASHFLOW_CAPITAL_ITEM_GROUPS', CashflowCapitalItemGroup as any, 'CashflowCapitalItemGroup'),
    cashflowCapitalItems: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.BusinessSimulatorCashflowCapitalItem), 'LOAD_ALL_BUSINESS_SIMULATOR_CASHFLOW_CAPITAL_ITEMS', CashflowCapitalItem as any, 'CashflowCapitalItem'),
    foresightScenarioBaselineCalculations: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.ForesightScenarioBaselineCalculation), 'LOAD_ALL_FORESIGHT_SCENARIO_BASELINE_CALCULATIONS', ForesightScenarioBaselineCalculation as any, 'ForesightScenarioBaselineCalculation'),
    foresightScenarios: sortedEntitiesReducer(ms, 'Name', switchReducers(ms,
        entityLoadAllReducer(ms, 'LOAD_ALL_FORESIGHT_SCENARIOS'),
        entityLoadReducer(ms, 'LOAD_FORESIGHT_SCENARIO'),
        (state, action: Action<'ADD_FORESIGHT_SCENARIO' | 'CLONE_FORESIGHT_SCENARIO'>) => {
            let newState = state || ms;

            if (action.type === 'ADD_FORESIGHT_SCENARIO') {
                if (action.payload.status === RequestStatus.SUCCEEDED && !action.payload.response) {
                    return entityAddReducer(ms, 'ADD_FORESIGHT_SCENARIO')(state, deepAssign(action, { payload: { status: RequestStatus.FAILED } }));
                }

                return entityAddReducer(ms, 'ADD_FORESIGHT_SCENARIO')(state, action);
            }

            if (action.type === 'CLONE_FORESIGHT_SCENARIO') {
                newState = deepAssign(newState, {
                    Data: {
                        [newState.Data.getIndexByID(action.payload.entityID)]: {
                            Custom: {
                                status: action.payload.status
                            }
                        }
                    }
                });
            }

            if (action.type === 'CLONE_FORESIGHT_SCENARIO' && action.payload.status === RequestStatus.SUCCEEDED && action.payload.response) {
                const data = newState.Data.copy();
                data.push(action.payload.response);

                newState = deepAssign(newState, { Data: data });
            }

            return newState;
        },
        entityEditReducer(ms, 'EDIT_FORESIGHT_SCENARIO'),
        childrenReducer(ms,
            combineReducers(m, {
                ElementTypes: sortableEntitiesReducer(ets, null, switchReducers(ets,
                    entityAddAllReducer(ets, 'ADD_ALL_FORESIGHT_SCENARIO_ELEMENT_TYPES'),
                    entityEditAllReducer(ets, 'EDIT_ALL_FORESIGHT_SCENARIO_ELEMENT_TYPES'),
                    entityDeleteAllReducer(ets, 'DELETE_ALL_FORESIGHT_SCENARIO_ELEMENT_TYPES')
                )),
                Levels: switchReducers(ls,
                    entityLoadReducer(ls, 'LOAD_FORESIGHT_SCENARIO_LEVEL'),
                    entityAddAllReducer(ls, 'ADD_ALL_FORESIGHT_SCENARIO_LEVELS'),
                    entityAddReducer(ls, 'ADD_FORESIGHT_SCENARIO_LEVEL'),
                    entityAddReducer(ls, 'ADD_FORESIGHT_SCENARIO_LEVEL_WITH_TRACKING_LEVEL_ITEM'),
                    entityEditAllReducer(ls, 'EDIT_ALL_FORESIGHT_SCENARIO_LEVELS'),
                    entityEditReducer(ls, 'EDIT_FORESIGHT_SCENARIO_LEVEL'),
                    childrenReducer(ls,
                        combineReducers(l, {
                            Elements: switchReducers(es,
                                entityLoadReducer(es, 'LOAD_FORESIGHT_SCENARIO_ELEMENT'),
                                entityAddReducer(es, 'ADD_FORESIGHT_SCENARIO_ELEMENT'),
                                entityEditReducer(es, 'EDIT_FORESIGHT_SCENARIO_ELEMENT'),
                                (state = es, action: Action<'ADD_ALL_FORESIGHT_SCENARIO_FACTORS'>) =>
                                    action.type === 'ADD_ALL_FORESIGHT_SCENARIO_FACTORS' ?
                                        action.payload.entities.reduce((state, entity, index) => elements(state, {
                                            type: 'ADD_FORESIGHT_SCENARIO_FACTOR',
                                            payload: action.payload.status !== RequestStatus.SUCCEEDED ?
                                                {
                                                    ...action.payload,
                                                    grandGrandParentID: action.payload.grandParentID,
                                                    grandParentID: action.payload.parentID,
                                                    parentID: entity.ElementID!,
                                                    entity
                                                } :
                                                {
                                                    ...action.payload,
                                                    grandGrandParentID: action.payload.grandParentID,
                                                    grandParentID: action.payload.parentID,
                                                    parentID: entity.ElementID!,
                                                    entity,
                                                    response: action.payload.response[index]
                                                }
                                        }), state) :
                                        state,
                                (state = es, action: Action<'DELETE_ALL_FORESIGHT_SCENARIO_FACTORS'>) =>
                                    action.type === 'DELETE_ALL_FORESIGHT_SCENARIO_FACTORS' ?
                                        action.payload.entityIDs.reduce((state, entityID) => elements(state, {
                                            type: 'DELETE_FORESIGHT_SCENARIO_FACTOR',
                                            payload: {
                                                ...action.payload,
                                                grandGrandParentID: action.payload.grandParentID,
                                                grandParentID: action.payload.parentID,
                                                parentID: state.find(({ Factors }) => Factors.getByID(entityID))?.ID ?? 0,
                                                entityID
                                            }
                                        }), state) :
                                        state,
                                elements,
                                entityDeleteReducer(es, 'DELETE_FORESIGHT_SCENARIO_ELEMENT')
                            )
                        }),
                        (state, action) => {
                            switch (action.type) {
                                case 'LOAD_FORESIGHT_SCENARIO_FACTOR':
                                case 'ADD_FORESIGHT_SCENARIO_FACTOR':
                                case 'EDIT_FORESIGHT_SCENARIO_FACTOR':
                                case 'DELETE_FORESIGHT_SCENARIO_FACTOR':
                                    return state.getIndexByID(action.payload.grandParentID);

                                case 'LOAD_FORESIGHT_SCENARIO_ELEMENT':
                                case 'ADD_FORESIGHT_SCENARIO_ELEMENT':
                                case 'EDIT_FORESIGHT_SCENARIO_ELEMENT':
                                case 'DELETE_FORESIGHT_SCENARIO_ELEMENT':
                                case 'ADD_ALL_FORESIGHT_SCENARIO_FACTORS':
                                case 'DELETE_ALL_FORESIGHT_SCENARIO_FACTORS':
                                    return state.getIndexByID(action.payload.parentID);

                                    // default:
                                    //     return state.getIndexByID(action.payload && ((action.payload as any).response || (action.payload as any).entityID));
                                default: return -1;
                            }
                        }
                    ),
                    (state, action: Action<'LOAD_MANY_FORESIGHT_SCENARIO_LEVELS'>) => {
                        let newState = (state || ls).copy();

                        if (action.type === 'LOAD_MANY_FORESIGHT_SCENARIO_LEVELS') {
                            if (action.payload.status !== RequestStatus.SUCCEEDED) {
                                action.payload.entityIDs.forEach((entityID) => {
                                    const entityIndex = newState.findIndex(({ ID, Custom }) => ID === entityID || Custom.internalID === entityID);

                                    newState = deepAssign(newState, {
                                        [entityIndex]: {
                                            Custom: {
                                                status: action.payload.status
                                            }
                                        }
                                    });
                                });
                            }
                            else {
                                action.payload.entityIDs.zip(action.payload.response, (entityID, entity) => {
                                    const entityIndex = newState.findIndex(({ ID, Custom }) => ID === entityID || Custom.internalID === entityID);

                                    if (entityIndex < 0) { newState.push(entity); }
                                    else { newState = deepAssign(newState, { [entityIndex]: entity }); }
                                });
                            }
                        }

                        return newState;
                    },
                    entityDeleteAllReducer(ls, 'DELETE_ALL_FORESIGHT_SCENARIO_LEVELS'),
                    entityDeleteReducer(ls, 'DELETE_FORESIGHT_SCENARIO_LEVEL')
                ),
                AccountReceivablesDistributions: sortedEntitiesReducer(ars, 'MonthOffset', switchReducers(ars,
                    entityAddAllReducer(ars, 'ADD_ALL_BUSINESS_SIMULATOR_ACCOUNT_RECEIVABLES_DISTRIBUTIONS'),
                    entityEditAllReducer(ars, 'EDIT_ALL_BUSINESS_SIMULATOR_ACCOUNT_RECEIVABLES_DISTRIBUTIONS'),
                    entityDeleteAllReducer(ars, 'DELETE_ALL_BUSINESS_SIMULATOR_ACCOUNT_RECEIVABLES_DISTRIBUTIONS')
                )),
                SalesReceivablesDistributions: sortedEntitiesReducer(srs, 'MonthOffset', switchReducers(srs,
                    entityAddAllReducer(srs, 'ADD_ALL_BUSINESS_SIMULATOR_SALES_RECEIVABLES_DISTRIBUTIONS'),
                    entityEditAllReducer(srs, 'EDIT_ALL_BUSINESS_SIMULATOR_SALES_RECEIVABLES_DISTRIBUTIONS'),
                    entityDeleteAllReducer(srs, 'DELETE_ALL_BUSINESS_SIMULATOR_SALES_RECEIVABLES_DISTRIBUTIONS')
                )),
                CapitalItemValues: switchReducers(civs,
                    entityLoadAllReducer(civs, 'LOAD_BUSINESS_SIMULATOR_CAPITAL_ITEM_VALUES_SLICE'),
                    entityAddAllReducer(civs, 'ADD_ALL_BUSINESS_SIMULATOR_CAPITAL_ITEM_VALUES'),
                    entityEditAllReducer(civs, 'EDIT_ALL_BUSINESS_SIMULATOR_CAPITAL_ITEM_VALUES'),
                    entityDeleteAllReducer(civs, 'DELETE_ALL_BUSINESS_SIMULATOR_CAPITAL_ITEM_VALUES'),
                    (state, action: Action<'COPY_CAPITAL_ITEM_VALUES_FROM_SCENARIO'>) => {
                        if (action.type === 'COPY_CAPITAL_ITEM_VALUES_FROM_SCENARIO' && action.payload.status === RequestStatus.SUCCEEDED) {
                            return deepAssign(state || civs, {
                                Custom: {
                                    status: RequestStatus.UNSYNCED
                                }
                            });
                        }

                        return state || civs;
                    }
                ),
                Custom: combineReducers(m.Custom, {
                    status: (state: RequestStatus = m.Custom.status, action: Action<'ADD_FORESIGHT_SCENARIO' | 'ADD_ALL_FORESIGHT_SCENARIO_LEVELS' | 'ADD_FORESIGHT_SCENARIO_LEVEL'>) =>
                        action.type === 'ADD_FORESIGHT_SCENARIO' || action.type === 'ADD_ALL_FORESIGHT_SCENARIO_LEVELS' || action.type === 'ADD_FORESIGHT_SCENARIO_LEVEL' ?
                            action.payload.status === RequestStatus.SUCCEEDED && action.payload.response ? RequestStatus.UNSYNCED as RequestStatus : state :
                            state,
                    export: (
                        state = m.Custom.export,
                        action: Action<
                        | 'EXPORT_FORESIGHT_SCENARIO_DATA'
                        | 'LOAD_FORESIGHT_SCENARIO_LEVEL'
                        | 'ADD_ALL_FORESIGHT_SCENARIO_LEVELS'
                        | 'ADD_FORESIGHT_SCENARIO_LEVEL'
                        | 'EDIT_ALL_FORESIGHT_SCENARIO_LEVELS'
                        | 'EDIT_FORESIGHT_SCENARIO_LEVEL'
                        | 'DELETE_ALL_FORESIGHT_SCENARIO_LEVELS'
                        | 'DELETE_FORESIGHT_SCENARIO_LEVEL'
                        | 'LOAD_FORESIGHT_SCENARIO_ELEMENT'
                        | 'ADD_FORESIGHT_SCENARIO_ELEMENT'
                        | 'EDIT_FORESIGHT_SCENARIO_ELEMENT'
                        | 'DELETE_FORESIGHT_SCENARIO_ELEMENT'
                        | 'ADD_ALL_FORESIGHT_SCENARIO_FACTORS'
                        | 'DELETE_ALL_FORESIGHT_SCENARIO_FACTORS'
                        | 'LOAD_FORESIGHT_SCENARIO_FACTOR'
                        | 'ADD_FORESIGHT_SCENARIO_FACTOR'
                        | 'EDIT_FORESIGHT_SCENARIO_FACTOR'
                        | 'DELETE_FORESIGHT_SCENARIO_FACTOR'
                        >
                    ) => {
                        switch (action.type) {
                            case 'EXPORT_FORESIGHT_SCENARIO_DATA':
                                return deepAssign(state, {
                                    URL: action.payload.status === RequestStatus.SUCCEEDED ? action.payload.response : '',
                                    Name: action.payload.filename,
                                    Custom: { status: action.payload.status }
                                });

                            case 'LOAD_FORESIGHT_SCENARIO_LEVEL':
                            case 'ADD_ALL_FORESIGHT_SCENARIO_LEVELS':
                            case 'ADD_FORESIGHT_SCENARIO_LEVEL':
                            case 'EDIT_ALL_FORESIGHT_SCENARIO_LEVELS':
                            case 'EDIT_FORESIGHT_SCENARIO_LEVEL':
                            case 'DELETE_ALL_FORESIGHT_SCENARIO_LEVELS':
                            case 'DELETE_FORESIGHT_SCENARIO_LEVEL':
                            case 'LOAD_FORESIGHT_SCENARIO_ELEMENT':
                            case 'ADD_FORESIGHT_SCENARIO_ELEMENT':
                            case 'EDIT_FORESIGHT_SCENARIO_ELEMENT':
                            case 'DELETE_FORESIGHT_SCENARIO_ELEMENT':
                            case 'ADD_ALL_FORESIGHT_SCENARIO_FACTORS':
                            case 'DELETE_ALL_FORESIGHT_SCENARIO_FACTORS':
                            case 'LOAD_FORESIGHT_SCENARIO_FACTOR':
                            case 'ADD_FORESIGHT_SCENARIO_FACTOR':
                            case 'EDIT_FORESIGHT_SCENARIO_FACTOR':
                            case 'DELETE_FORESIGHT_SCENARIO_FACTOR':
                                return deepAssign(state, m.Custom.export);

                            default:
                                return state;
                        }
                    },
                    calculation: switchReducers(m.Custom.calculation,
                        networkActionReducer(m.Custom.calculation, 'CALCULATE_FORESIGHT_SCENARIO_CASHFLOW'),
                        networkActionReducer(m.Custom.calculation, 'CALCULATE_FORESIGHT_SCENARIO_FORECAST'),
                        networkActionReducer(m.Custom.calculation, 'COPY_CAPITAL_ITEM_VALUES_FROM_SCENARIO'),
                        networkActionReducer(m.Custom.calculation, 'COPY_BUDGET_FROM_FORESIGHT_SCENARIO_ELEMENT_SOURCE_INDICATOR')
                    )
                })
            }),
            (state, action) => {
                switch (action.type) {
                    case 'LOAD_FORESIGHT_SCENARIO_FACTOR':
                    case 'ADD_FORESIGHT_SCENARIO_FACTOR':
                    case 'EDIT_FORESIGHT_SCENARIO_FACTOR':
                    case 'DELETE_FORESIGHT_SCENARIO_FACTOR':
                        return state.Data.getIndexByID(action.payload.grandGrandParentID);

                    case 'LOAD_FORESIGHT_SCENARIO_ELEMENT':
                    case 'ADD_FORESIGHT_SCENARIO_ELEMENT':
                    case 'EDIT_FORESIGHT_SCENARIO_ELEMENT':
                    case 'DELETE_FORESIGHT_SCENARIO_ELEMENT':
                    case 'ADD_ALL_FORESIGHT_SCENARIO_FACTORS':
                    case 'DELETE_ALL_FORESIGHT_SCENARIO_FACTORS':
                    case 'COPY_BUDGET_FROM_FORESIGHT_SCENARIO_ELEMENT_SOURCE_INDICATOR':
                        return state.Data.getIndexByID(action.payload.grandParentID);

                    case 'LOAD_FORESIGHT_SCENARIO_LEVEL':
                    case 'LOAD_MANY_FORESIGHT_SCENARIO_LEVELS':
                    case 'ADD_ALL_FORESIGHT_SCENARIO_LEVELS':
                    case 'ADD_FORESIGHT_SCENARIO_LEVEL':
                    case 'ADD_FORESIGHT_SCENARIO_LEVEL_WITH_TRACKING_LEVEL_ITEM':
                    case 'EDIT_ALL_FORESIGHT_SCENARIO_LEVELS':
                    case 'EDIT_FORESIGHT_SCENARIO_LEVEL':
                    case 'DELETE_ALL_FORESIGHT_SCENARIO_LEVELS':
                    case 'DELETE_FORESIGHT_SCENARIO_LEVEL':
                    case 'ADD_ALL_BUSINESS_SIMULATOR_ACCOUNT_RECEIVABLES_DISTRIBUTIONS':
                    case 'EDIT_ALL_BUSINESS_SIMULATOR_ACCOUNT_RECEIVABLES_DISTRIBUTIONS':
                    case 'DELETE_ALL_BUSINESS_SIMULATOR_ACCOUNT_RECEIVABLES_DISTRIBUTIONS':
                    case 'ADD_ALL_BUSINESS_SIMULATOR_SALES_RECEIVABLES_DISTRIBUTIONS':
                    case 'EDIT_ALL_BUSINESS_SIMULATOR_SALES_RECEIVABLES_DISTRIBUTIONS':
                    case 'DELETE_ALL_BUSINESS_SIMULATOR_SALES_RECEIVABLES_DISTRIBUTIONS':
                    case 'LOAD_BUSINESS_SIMULATOR_CAPITAL_ITEM_VALUES_SLICE':
                    case 'ADD_ALL_BUSINESS_SIMULATOR_CAPITAL_ITEM_VALUES':
                    case 'EDIT_ALL_BUSINESS_SIMULATOR_CAPITAL_ITEM_VALUES':
                    case 'DELETE_ALL_BUSINESS_SIMULATOR_CAPITAL_ITEM_VALUES':
                        return state.Data.getIndexByID(action.payload.parentID);

                    case 'EXPORT_FORESIGHT_SCENARIO_DATA':
                    case 'COPY_CAPITAL_ITEM_VALUES_FROM_SCENARIO':
                        return state.Data.getIndexByID(action.payload.entityID);

                    default:
                        return state.Data.getIndexByID(action.payload && ((action.payload as any).response || (action.payload as any).entityID));

                }
            }
        ),
        (state, action: Action<'EDIT_FORESIGHT_SCENARIO'>) => {
            const defaultState = state || ms;

            if (action.type === 'EDIT_FORESIGHT_SCENARIO' && action.payload.entity.IsDefault && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(defaultState, {
                    Data: defaultState.Data.map(data => ({ ...data, IsDefault: data.ID === action.payload.entity.ID }))
                });
            }

            return defaultState;
        },
        entityDeleteReducer(ms, 'DELETE_FORESIGHT_SCENARIO'),
        entityVoidAllReducer(ms, 'EDIT_CASHFLOW_FORECAST_SETTINGS'),
        entityVoidAllReducer(ms, 'VOID_ALL_FORESIGHT_SCENARIOS')
    ))
});
